import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, File, Download, Trash2, FolderOpen, AlertCircle, Calendar, X, Edit2, Search, Receipt, Sparkles, Eye, Loader2, Banknote } from "lucide-react";
import { api, apiClient } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { useAuth } from "@/lib/auth";
import { PageHeader, Empty, Field } from "@/components/UI";
import { formatDate, formatCurrency } from "@/lib/format";

const FOLDERS = [
  "Company Legal Documents",
  "Admin Documents",
  "EFRIS Documents",
  "Receipts",
  "Books of Accounts",
  "LPOs",
  "Past Experiences",
  "Manufacturer Authorization",
  "Bank Documents",
];

// Common compliance categories used by Ugandan procurement
const CATEGORIES = [
  "Receipt",
  "Labour / Wages",
  "Website & Software Development",
  "Employee Transport",
  "Internet & Data",
  "Airtime & Communication",
  "Hotels & Accommodation",
  "Meals & Lunches",
  "Flights & Travel",
  "Meetings & Events",
  "Fuel",
  "Office Supplies & Stationery",
  "Equipment Purchase",
  "Repairs & Maintenance",
  "Marketing & Advertising",
  "Utilities (Power & Water)",
  "Rent",
  "Medical",
  "Training & Workshops",
  "EFRIS e-Receipt / e-Invoice",
  "Cash Book / Ledger",
  "Trading Licence",
  "Tax Clearance",
  "VAT Certificate",
  "EFRIS Registration",
  "NSSF Clearance",
  "NITA-U Registration",
  "URSB Certificate",
  "Company Form 20",
  "Director's NIN",
  "Bank Reference",
  "Audited Financials",
  "Trial Balance",
  "Profit & Loss Statement",
  "Balance Sheet",
  "Bank Statement",
  "Other",
];

const RECEIPT_FOLDERS = ["Receipts", "EFRIS Documents"];

function daysUntil(iso) {
  if (!iso) return null;
  const t = new Date(iso);
  if (isNaN(t)) return null;
  const ms = t.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0);
  return Math.round(ms / 86400000);
}

function ExpiryBadge({ date }) {
  const d = daysUntil(date);
  if (d === null) return null;
  let bg = "var(--green-50)", color = "var(--green-700)", label;
  if (d < 0) {
    bg = "#fef2f2"; color = "#b91c1c"; label = `Expired ${-d}d ago`;
  } else if (d <= 7) {
    bg = "#fef2f2"; color = "#b91c1c"; label = `${d}d left`;
  } else if (d <= 30) {
    bg = "#fef3c7"; color = "#92400e"; label = `${d}d left`;
  } else if (d <= 60) {
    bg = "#fef3c7"; color = "#92400e"; label = `${d}d left`;
  } else {
    label = `${d}d left`;
  }
  return (
    <span
      className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
      style={{ background: bg, color }}
      data-testid="expiry-badge"
    >
      {label}
    </span>
  );
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [folder, setFolder] = useState(FOLDERS[0]);
  const { user } = useAuth();
  const finance = user?.role === "admin" || !!user?.hr_access;
  const visibleFolders = finance ? FOLDERS : FOLDERS.filter((f) => f !== "Books of Accounts");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [dragTarget, setDragTarget] = useState(null);

  const openWithFiles = (targetFolder, fileList) => {
    const fs = Array.from(fileList || []).filter((f) => f.size >= 0);
    if (!fs.length) return;
    if (targetFolder && targetFolder !== folder) setFolder(targetFolder);
    setUploadForm({ ...EMPTY_FORM, open: true, files: fs });
  };

  const dropProps = (target) => ({
    onDragOver: (e) => { e.preventDefault(); setDragTarget(target); },
    onDragLeave: (e) => { e.preventDefault(); setDragTarget((t) => (t === target ? null : t)); },
    onDrop: (e) => {
      e.preventDefault();
      setDragTarget(null);
      openWithFiles(target === "__panel__" ? folder : target, e.dataTransfer.files);
    },
  });
  const [gStatus, setGStatus] = useState(null);
  const EMPTY_FORM = { open: false, category: "", description: "", expires: "no", expiry_date: "", renew_reminder_date: "", amount: "", doc_date: "", vendor: "", files: [] };
  const [uploadForm, setUploadForm] = useState(EMPTY_FORM);
  const [search, setSearch] = useState("");
  const EMPTY_EDIT = { open: false, doc: null, description: "", category: "", expires: "no", expiry_date: "", renew_reminder_date: "", amount: "", doc_date: "", saving: false };
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState({ open: false, url: null, doc: null, kind: "pdf" });
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [exportMonth, setExportMonth] = useState("");

  const scanReceiptFile = async (fileArg) => {
    const file = fileArg || uploadForm.files[0];
    if (!file) return;
    setScanning(true);
    try {
      const r = await api.scanReceipt(file);
      setUploadForm((f) => ({
        ...f,
        amount: r.amount != null ? String(r.amount) : f.amount,
        doc_date: r.doc_date || f.doc_date,
        description: r.description || f.description,
        vendor: r.vendor || f.vendor,
        category: r.category && r.category !== "Receipt"
          ? r.category
          : f.category || (folder === "EFRIS Documents" ? "EFRIS e-Receipt / e-Invoice" : "Receipt"),
      }));
      toast(
        r.original_currency
          ? `Converted ${r.original_currency} ${Number(r.original_amount).toLocaleString()} → UGX ${Number(r.amount).toLocaleString()} @ ${Number(r.fx_rate).toLocaleString()}`
          : r.amount != null
            ? `Read: ${r.description || "receipt"} — ${Number(r.amount).toLocaleString()} ${r.currency}`
            : "Read the receipt but couldn't find a total — check the fields",
        r.amount != null ? "success" : "info"
      );
    } catch (e) {
      toast(e?.response?.data?.detail || "AI could not read this receipt", "error");
    } finally {
      setScanning(false);
    }
  };

  const isScannable = (f) => f && (f.type.startsWith("image/") || f.type === "application/pdf");

  const findDuplicate = (amount, docDate) => {
    if (!amount || !docDate) return null;
    return docs.find((d) => d.amount != null && Number(amount) === d.amount && d.doc_date === docDate) || null;
  };
  const dupe = uploadForm.open ? findDuplicate(uploadForm.amount, uploadForm.doc_date) : null;

  const exportExpenses = async (month) => {
    try {
      const res = await api.exportExpenses(activeYear, month || undefined);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `expenses_${activeYear}${month ? `_${String(month).padStart(2, "0")}` : ""}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Export failed", "error");
    }
  };

  const isPreviewable = (d) =>
    (d.mime_type || "").startsWith("image/") || (d.mime_type || "") === "application/pdf" ||
    /\.(pdf|png|jpe?g|webp|gif)$/i.test(d.file_name || "");

  const openPreview = async (d) => {
    try {
      const res = await apiClient.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const kind = (d.mime_type || "").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(d.file_name)
        ? "image" : "pdf";
      const blob = kind === "pdf" ? new Blob([res.data], { type: "application/pdf" }) : res.data;
      setPreview({ open: true, url: URL.createObjectURL(blob), doc: d, kind });
    } catch {
      toast("Preview failed", "error");
    }
  };
  const closePreview = () => {
    if (preview.url) URL.revokeObjectURL(preview.url);
    setPreview({ open: false, url: null, doc: null, kind: "pdf" });
  };
  const fileInput = useRef(null);
  const { toast } = useToast();

  const load = useCallback(
    () => api.listDocuments().then(setDocs).catch(() => toast("Failed to load", "error")),
    [toast],
  );

  useEffect(() => {
    load();
    api.googleStatus().then(setGStatus).catch(() => {});
  }, [load]);

  const openUpload = () => setUploadForm({ ...EMPTY_FORM, open: true });

  const EMPTY_CASH = { open: false, description: "", amount: "", doc_date: new Date().toISOString().slice(0, 10), vendor: "", category: "Cash Book / Ledger", saving: false };
  const [cashForm, setCashForm] = useState(EMPTY_CASH);

  const saveCashExpense = async () => {
    if (!cashForm.description.trim() || !(parseFloat(cashForm.amount) > 0)) {
      toast("Add a description and an amount above zero", "error");
      return;
    }
    setCashForm((f) => ({ ...f, saving: true }));
    try {
      await api.addCashExpense({
        description: cashForm.description,
        amount: parseFloat(cashForm.amount),
        doc_date: cashForm.doc_date,
        vendor: cashForm.vendor,
        category: cashForm.category,
      });
      toast("Cash expense recorded in Books of Accounts", "success");
      setCashForm(EMPTY_CASH);
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Failed to record expense", "error");
      setCashForm((f) => ({ ...f, saving: false }));
    }
  };

  const confirmUpload = async () => {
    if (!uploadForm.files.length) return;
    if (uploadForm.files.length === 1 && dupe && !window.confirm(
      `Possible duplicate: “${dupe.file_name}” in ${dupe.folder} already has the same amount and date. Upload anyway?`
    )) return;
    setUploading(true);
    let ok = 0;
    let failed = 0;
    const n = uploadForm.files.length;
    for (let i = 0; i < n; i++) {
      setUploadProgress(n > 1 ? `Uploading ${i + 1} of ${n}…` : "Uploading…");
      try {
        const fd = new FormData();
        fd.append("file", uploadForm.files[i]);
        fd.append("folder", folder);
        if (uploadForm.description) fd.append("description", uploadForm.description);
        if (uploadForm.category) fd.append("category", uploadForm.category);
        if (uploadForm.expires === "yes") {
          if (uploadForm.expiry_date) fd.append("expiry_date", uploadForm.expiry_date);
          if (uploadForm.renew_reminder_date) fd.append("renew_reminder_date", uploadForm.renew_reminder_date);
        }
        if (uploadForm.amount && n === 1) fd.append("amount", uploadForm.amount);
        if (uploadForm.doc_date) fd.append("doc_date", uploadForm.doc_date);
        if (uploadForm.vendor) fd.append("vendor", uploadForm.vendor);
        await api.uploadDocument(fd);
        ok++;
      } catch {
        failed++;
        toast(`Upload failed: ${uploadForm.files[i].name}`, "error");
      }
    }
    if (ok) toast(ok === 1 ? "Uploaded" : `${ok} files uploaded — use the pencil icon to edit details on each`, "success");
    load();
    if (!failed) setUploadForm(EMPTY_FORM);
    setUploading(false);
    setUploadProgress("");
    if (fileInput.current) fileInput.current.value = "";
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.file_name}?`)) return;
    try {
      await api.deleteDocument(d.id);
      toast("Deleted", "success");
      load();
    } catch {
      toast("Delete failed", "error");
    }
  };

  const download = async (d) => {
    try {
      const res = await apiClient.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Download failed", "error");
    }
  };

  const openEdit = (d) =>
    setEditForm({
      open: true, doc: d, saving: false,
      description: d.description || "",
      category: d.category || "",
      expires: d.expiry_date ? "yes" : "no",
      expiry_date: d.expiry_date || "",
      renew_reminder_date: d.renew_reminder_date || "",
      amount: d.amount != null ? String(d.amount) : "",
      doc_date: d.doc_date || "",
    });

  const saveEdit = async () => {
    setEditForm((f) => ({ ...f, saving: true }));
    try {
      await api.updateDocument(editForm.doc.id, {
        description: editForm.description,
        category: editForm.category,
        expiry_date: editForm.expires === "yes" ? editForm.expiry_date : "",
        renew_reminder_date: editForm.expires === "yes" ? editForm.renew_reminder_date : "",
        amount: editForm.amount,
        doc_date: editForm.doc_date,
      });
      toast("Details updated", "success");
      setEditForm(EMPTY_EDIT);
      load();
    } catch {
      toast("Update failed", "error");
      setEditForm((f) => ({ ...f, saving: false }));
    }
  };

  const q = search.trim().toLowerCase();
  const searching = q.length > 0;
  const filtered = searching
    ? docs.filter((d) =>
        [d.file_name, d.description, d.category, d.folder]
          .some((s) => (s || "").toLowerCase().includes(q)))
    : docs.filter((d) => d.folder === folder);

  const showTotals = !searching && (RECEIPT_FOLDERS.includes(folder) || folder === "Books of Accounts");

  // "Books of Accounts" is grouped by Ugandan financial year (July – June)
  const fyLabel = (iso) => {
    const y = parseInt((iso || "").slice(0, 4), 10);
    const m = parseInt((iso || "").slice(5, 7), 10);
    if (!y) return "Undated";
    const start = m >= 7 ? y : y - 1;
    return `${start}/${String((start + 1) % 100).padStart(2, "0")}`;
  };
  const displayGroups = (!searching && folder === "Books of Accounts")
    ? Object.entries(
        filtered.reduce((acc, d) => {
          const k = fyLabel(d.doc_date || (d.created_at || "").slice(0, 10));
          (acc[k] = acc[k] || []).push(d);
          return acc;
        }, {})
      ).sort((a, b) => (a[0] === "Undated" ? 1 : b[0] === "Undated" ? -1 : b[0].localeCompare(a[0])))
    : [[null, filtered]];

  // Unified expense totals — receipts + cash expenses + payroll across ALL folders
  const amountDocs = showTotals ? (docs || []).filter((d) => d.amount != null) : [];
  const years = [...new Set(amountDocs.map((d) => (d.doc_date || (d.created_at || "").slice(0, 10)).slice(0, 4)).filter(Boolean))].sort().reverse();
  const activeYear = years.includes(year) ? year : (years[0] || year);
  const monthTotals = Array.from({ length: 12 }, () => 0);
  let yearTotal = 0;
  amountDocs.forEach((d) => {
    const iso = d.doc_date || (d.created_at || "").slice(0, 10);
    if (iso.slice(0, 4) !== activeYear) return;
    const m = parseInt(iso.slice(5, 7), 10) - 1;
    if (m >= 0 && m < 12) {
      monthTotals[m] += d.amount;
      yearTotal += d.amount;
    }
  });
  const maxMonth = Math.max(...monthTotals, 1);
  const MONTH_LABELS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

  return (
    <div data-testid="page-documents">
      <PageHeader
        icon={FolderOpen}
        title="Document Vault"
        subtitle="Central storage for compliance certificates and bid attachments"
        action={
          <div className="flex gap-2">
            {finance && (
              <button
                data-testid="cash-expense-btn"
                onClick={() => setCashForm({ ...EMPTY_CASH, open: true })}
                className="btn btn-secondary"
              >
                <Banknote size={14} /> Cash expense
              </button>
            )}
            <button
              data-testid="upload-btn"
              onClick={openUpload}
              disabled={uploading}
              className="btn btn-primary"
            >
              <Upload size={14} /> {uploading ? "Uploading…" : "Upload file"}
            </button>
          </div>
        }
      />

      {cashForm.open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => !cashForm.saving && setCashForm(EMPTY_CASH)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="font-bold text-sm flex items-center gap-2"><Banknote size={15} /> Record cash expense</div>
              <button onClick={() => setCashForm(EMPTY_CASH)} data-testid="cash-expense-close">
                <X size={16} className="text-slate-400" />
              </button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto flex-1 min-h-0">
              <div className="text-xs muted -mt-1">
                For cash dispensed for operations (fuel, transport, airtime, petty purchases…).
                It lands in <b>Books of Accounts</b> and rolls into your expense totals and CSV export.
                Have a scanned receipt? Use <b>Upload file</b> instead.
              </div>
              <Field label="What was the cash for? *">
                <input data-testid="cash-expense-desc" className="input" placeholder="e.g. Fuel for site survey — Entebbe"
                  value={cashForm.description}
                  onChange={(e) => setCashForm({ ...cashForm, description: e.target.value })} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Amount (UGX) *">
                  <input data-testid="cash-expense-amount" type="number" min="0" className="input" placeholder="e.g. 150000"
                    value={cashForm.amount}
                    onChange={(e) => setCashForm({ ...cashForm, amount: e.target.value })} />
                </Field>
                <Field label="Date">
                  <input data-testid="cash-expense-date" type="date" className="input"
                    value={cashForm.doc_date}
                    onChange={(e) => setCashForm({ ...cashForm, doc_date: e.target.value })} />
                </Field>
              </div>
              <Field label="Paid to / received by (optional)">
                <input data-testid="cash-expense-vendor" className="input" placeholder="e.g. Shell Bugolobi / John (driver)"
                  value={cashForm.vendor}
                  onChange={(e) => setCashForm({ ...cashForm, vendor: e.target.value })} />
              </Field>
              <Field label="Category">
                <select data-testid="cash-expense-category" className="input"
                  value={cashForm.category}
                  onChange={(e) => setCashForm({ ...cashForm, category: e.target.value })}>
                  {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </Field>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button className="btn btn-secondary" onClick={() => setCashForm(EMPTY_CASH)} data-testid="cash-expense-cancel">Cancel</button>
              <button className="btn btn-primary" onClick={saveCashExpense} disabled={cashForm.saving} data-testid="cash-expense-save">
                {cashForm.saving ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />} Record expense
              </button>
            </div>
          </div>
        </div>
      )}

      {gStatus && !gStatus.configured && (
        <div className="card card-padded mb-5 border-l-4" style={{ borderLeftColor: "#f59e0b" }}>
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm">Google Drive not yet connected</div>
              <div className="text-xs muted mt-1">
                Files are stored locally for now. Configure Google OAuth in the
                backend to sync to Drive automatically.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="vault-search"
          type="text"
          className="input pl-9 w-full lg:max-w-md"
          placeholder="Search all folders — name, description or category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {searching && (
          <button
            data-testid="vault-search-clear"
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 lg:hidden"
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <div className="card card-tight" data-testid="folder-list">
          {visibleFolders.map((f) => {
            const count = docs.filter((d) => d.folder === f).length;
            const active = folder === f;
            return (
              <button
                key={f}
                data-testid={`folder-${f.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setFolder(f)}
                {...dropProps(f)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition mb-0.5 ${
                  dragTarget === f
                    ? "bg-[var(--green-50)] ring-2 ring-[var(--green-700)] font-semibold"
                    : active
                    ? "bg-[var(--green-50)] font-semibold"
                    : "hover:bg-slate-50"
                }`}
                style={active ? { color: "var(--green-700)" } : { color: "var(--ink-700)" }}
              >
                <span className="flex items-center gap-2 truncate text-left">
                  <FolderOpen size={14} />
                  <span className="truncate">{f}</span>
                </span>
                <span className="text-[11px] muted">{count}</span>
              </button>
            );
          })}
        </div>

        <div
          className={`lg:col-span-3 card overflow-hidden relative ${dragTarget === "__panel__" ? "ring-2 ring-[var(--green-700)]" : ""}`}
          {...dropProps("__panel__")}
          data-testid="doc-panel-dropzone"
        >
          {dragTarget === "__panel__" && (
            <div className="absolute inset-0 z-10 bg-[var(--green-50)]/80 flex items-center justify-center pointer-events-none">
              <div className="text-sm font-bold" style={{ color: "var(--green-700)" }}>
                Drop files to upload to “{folder}”
              </div>
            </div>
          )}
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="h2">{searching ? `Search: “${search.trim()}”` : folder}</div>
            <div className="text-xs muted mt-0.5">
              {filtered.length} file{filtered.length === 1 ? "" : "s"}
              {searching && " across all folders"}
            </div>
          </div>
          {showTotals && amountDocs.length > 0 && (
            <div className="px-5 py-3 border-b border-slate-100 bg-[var(--green-50)]" data-testid="receipt-totals">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: "var(--green-700)" }}>
                  <Receipt size={12} /> All Expenses — {activeYear}
                  <span className="normal-case font-semibold muted">(receipts + cash + payroll)</span>
                  <span className="normal-case font-extrabold text-sm ml-1">{formatCurrency(yearTotal, "UGX")}</span>
                </div>
                {years.length > 1 && (
                  <select
                    data-testid="expense-year"
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white"
                    value={activeYear}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                )}
                <div className="flex items-center gap-1.5">
                  <select
                    data-testid="export-month"
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white"
                    value={exportMonth}
                    onChange={(e) => setExportMonth(e.target.value)}
                  >
                    <option value="">Whole year</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(2000, i, 1).toLocaleDateString(undefined, { month: "long" })}
                      </option>
                    ))}
                  </select>
                  <button
                    data-testid="export-expenses-btn"
                    onClick={() => exportExpenses(exportMonth ? Number(exportMonth) : null)}
                    className="text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1 font-semibold hover:border-[#0e6939] flex items-center gap-1"
                    style={{ color: "var(--green-700)" }}
                  >
                    <Download size={11} /> Export CSV
                  </button>
                </div>
              </div>
              <div className="flex items-end gap-1.5 h-16" data-testid="expense-chart">
                {monthTotals.map((t, i) => (
                  <div key={`m-${i}`} className="flex-1 flex flex-col items-center gap-0.5" title={`${MONTH_LABELS[i]}: ${formatCurrency(t, "UGX")}`}>
                    <div
                      className="w-full rounded-t"
                      style={{
                        height: `${t > 0 ? Math.max(8, (t / maxMonth) * 48) : 2}px`,
                        background: t > 0 ? "var(--green-700)" : "#d6e5dc",
                      }}
                    />
                    <span className="text-[9px] muted">{MONTH_LABELS[i]}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                {monthTotals.map((t, i) => t > 0 && (
                  <span key={`mt-${i}`} className="text-[10px] muted">
                    <b>{new Date(2000, i, 1).toLocaleDateString(undefined, { month: "short" })}</b>{" "}
                    {formatCurrency(t, "UGX")}
                  </span>
                ))}
              </div>
            </div>
          )}
          {filtered.length === 0 ? (
            <Empty
              icon={FolderOpen}
              title={searching ? "No matches" : "No files here yet"}
              description={searching
                ? "Try a different name, description or category."
                : `Upload to "${folder}" to keep them at hand for proposals.`}
              action={
                <button onClick={openUpload} className="btn btn-primary">
                  <Upload size={14} /> Upload to this folder
                </button>
              }
            />
          ) : (
            <div>
              {displayGroups.map(([fy, list]) => (
                <div key={fy || "all"}>
                  {fy && (
                    <div
                      data-testid={`fy-group-${fy.replace(/\W/g, "-")}`}
                      className="px-5 py-2 bg-slate-50 border-y border-slate-100 text-[11px] font-bold uppercase tracking-wider"
                      style={{ color: "var(--green-700)" }}
                    >
                      Financial Year {fy}
                      <span className="font-normal normal-case text-slate-400"> · {list.length} file{list.length === 1 ? "" : "s"} · Jul–Jun</span>
                      {list.some((d) => d.amount != null) && (
                        <span className="font-bold normal-case float-right" data-testid={`fy-total-${fy.replace(/\W/g, "-")}`}>
                          Total {formatCurrency(list.reduce((a, d) => a + (d.amount || 0), 0), "UGX")}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="divide-y divide-slate-100">
                    {list.map((d) => (
                <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <File size={16} className="text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate flex items-center gap-2">
                      <span className="truncate">{d.file_name}</span>
                      <ExpiryBadge date={d.expiry_date} />
                    </div>
                    {d.description && (
                      <div className="text-xs text-slate-600 truncate">{d.description}</div>
                    )}
                    <div className="text-xs muted">
                      {searching && <span className="font-medium">{d.folder} · </span>}
                      {d.category && <span className="font-medium" style={{ color: "var(--green-700)" }}>{d.category} · </span>}
                      {d.amount != null && <span className="font-semibold" style={{ color: "var(--green-700)" }}>{formatCurrency(d.amount, "UGX")} · </span>}
                      {(d.file_size / 1024).toFixed(1)} KB · {formatDate(d.doc_date || d.created_at)}
                      {d.uploaded_by && <span data-testid={`uploader-${d.id}`}> · by {d.uploaded_by}</span>}
                      {d.expiry_date ? <span> · expires {d.expiry_date}</span> : <span> · no expiration</span>}
                      {d.renew_reminder_date && <span> · renew from {d.renew_reminder_date}</span>}
                    </div>
                  </div>
                  <button
                    data-testid={`edit-doc-${d.id}`}
                    onClick={() => openEdit(d)}
                    className="text-slate-500 hover:text-[#0e6939] p-1.5"
                    title="Edit details"
                  >
                    <Edit2 size={15} />
                  </button>
                  {isPreviewable(d) && (
                    <button
                      data-testid={`preview-doc-${d.id}`}
                      onClick={() => openPreview(d)}
                      className="text-slate-500 hover:text-[#0e6939] p-1.5"
                      title="Preview in browser"
                    >
                      <Eye size={15} />
                    </button>
                  )}
                  <button
                    data-testid={`dl-${d.id}`}
                    onClick={() => download(d)}
                    className="text-slate-500 hover:text-[#0e6939] p-1.5"
                  >
                    <Download size={15} />
                  </button>
                  <button
                    data-testid={`del-${d.id}`}
                    onClick={() => remove(d)}
                    className="text-slate-500 hover:text-red-600 p-1.5"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {uploadForm.open && (
        <div
          data-testid="upload-modal"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !uploading && setUploadForm(EMPTY_FORM)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="font-bold text-sm">Upload to {folder}</div>
              <button onClick={() => setUploadForm(EMPTY_FORM)}>
                <X size={16} className="text-slate-400" />
              </button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto flex-1 min-h-0">
              <Field label="Files * (you can select several at once)">
                <input
                  data-testid="upload-file"
                  ref={fileInput}
                  type="file"
                  multiple
                  onChange={(e) => {
                    const fs = Array.from(e.target.files || []);
                    setUploadForm((prev) => ({ ...prev, files: fs }));
                    // Auto-read receipts with AI when a single file is picked
                    if (fs.length === 1 && RECEIPT_FOLDERS.includes(folder) && isScannable(fs[0])) {
                      scanReceiptFile(fs[0]);
                    }
                  }}
                  className="input"
                />
                {uploadForm.files.length > 0 && (
                  <div className="mt-1.5 space-y-1 max-h-36 overflow-y-auto pr-0.5" data-testid="selected-files">
                    {uploadForm.files.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 text-[11px] bg-slate-50 rounded px-2 py-1">
                        <span className="min-w-0 truncate">
                          <span className="font-semibold">{f.name}</span>{" "}
                          <span className="muted">({(f.size / 1024).toFixed(1)} KB)</span>
                        </span>
                        <button
                          type="button"
                          data-testid={`remove-file-${i}`}
                          onClick={() => setUploadForm((prev) => ({ ...prev, files: prev.files.filter((_, idx) => idx !== i) }))}
                          className="text-slate-400 hover:text-red-500 shrink-0"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    {uploadForm.files.length > 1 && (
                      <div className="text-[10px] muted">
                        The details below apply to all {uploadForm.files.length} files — you can edit each one afterwards with the pencil icon.
                      </div>
                    )}
                    {uploadForm.files.length === 1 && RECEIPT_FOLDERS.includes(folder) && isScannable(uploadForm.files[0]) && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          data-testid="scan-receipt-btn"
                          onClick={() => scanReceiptFile()}
                          disabled={scanning}
                          className="btn btn-ghost text-xs shrink-0 border border-slate-200"
                        >
                          {scanning ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                          {scanning ? "Reading with AI…" : "Re-read with AI"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </Field>
              <Field label="Short description (optional)">
                <input
                  data-testid="upload-description"
                  type="text"
                  maxLength={300}
                  className="input"
                  placeholder="e.g. URA EFRIS receipt for office furniture — May 2026"
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                />
              </Field>
              <Field label="Category (optional)">
                <select
                  data-testid="upload-category"
                  className="input"
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                >
                  <option value="">— Choose —</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
              {RECEIPT_FOLDERS.includes(folder) && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Amount (UGX, optional)">
                    <input
                      data-testid="upload-amount"
                      type="number"
                      min="0"
                      className="input"
                      placeholder="e.g. 450000"
                      value={uploadForm.amount}
                      onChange={(e) => setUploadForm({ ...uploadForm, amount: e.target.value })}
                    />
                  </Field>
                  <Field label="Receipt date (optional)">
                    <input
                      data-testid="upload-doc-date"
                      type="date"
                      className="input"
                      value={uploadForm.doc_date}
                      onChange={(e) => setUploadForm({ ...uploadForm, doc_date: e.target.value })}
                    />
                  </Field>
                </div>
              )}
              {dupe && (
                <div
                  data-testid="duplicate-warning"
                  className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2"
                >
                  <AlertCircle size={14} className="shrink-0 mt-0.5 text-amber-600" />
                  <span>
                    <b>Possible duplicate:</b> “{dupe.file_name}” in {dupe.folder} already has this
                    amount ({formatCurrency(dupe.amount, "UGX")}) and date ({dupe.doc_date}).
                  </span>
                </div>
              )}
              <Field label="Expiration">
                <div className="flex gap-4 py-1" data-testid="expires-choice">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      data-testid="expires-no"
                      type="radio"
                      name="expires"
                      checked={uploadForm.expires === "no"}
                      onChange={() => setUploadForm({ ...uploadForm, expires: "no", expiry_date: "", renew_reminder_date: "" })}
                    />
                    No expiration
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      data-testid="expires-yes"
                      type="radio"
                      name="expires"
                      checked={uploadForm.expires === "yes"}
                      onChange={() => setUploadForm({ ...uploadForm, expires: "yes" })}
                    />
                    Has an expiry date
                  </label>
                </div>
              </Field>
              {uploadForm.expires === "yes" && (
                <>
                  <Field label="Expiry date *">
                    <div className="relative">
                      <Calendar size={14} className="absolute left-3 top-3 text-slate-400" />
                      <input
                        data-testid="upload-expiry"
                        type="date"
                        className="input pl-9"
                        value={uploadForm.expiry_date}
                        onChange={(e) => setUploadForm({ ...uploadForm, expiry_date: e.target.value })}
                      />
                    </div>
                  </Field>
                  <Field label="Remind me to renew on (optional)">
                    <div className="relative">
                      <Calendar size={14} className="absolute left-3 top-3 text-slate-400" />
                      <input
                        data-testid="upload-renew-date"
                        type="date"
                        max={uploadForm.expiry_date || undefined}
                        className="input pl-9"
                        value={uploadForm.renew_reminder_date}
                        onChange={(e) => setUploadForm({ ...uploadForm, renew_reminder_date: e.target.value })}
                      />
                    </div>
                    <div className="text-[11px] muted mt-1">
                      You'll get a bell notification on this date so renewal starts in good time.
                    </div>
                  </Field>
                </>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
              <button
                onClick={() => setUploadForm(EMPTY_FORM)}
                className="btn btn-ghost"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                data-testid="upload-confirm"
                onClick={confirmUpload}
                className="btn btn-primary"
                disabled={uploading || uploadForm.files.length === 0 || (uploadForm.expires === "yes" && !uploadForm.expiry_date)}
              >
                {uploading
                  ? (uploadProgress || "Uploading…")
                  : uploadForm.files.length > 1
                    ? `Upload ${uploadForm.files.length} files`
                    : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
      {preview.open && (
        <div
          data-testid="doc-preview-modal"
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={closePreview}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="font-bold text-sm truncate pr-3">{preview.doc?.file_name}</div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="preview-download"
                  onClick={() => download(preview.doc)}
                  className="btn btn-ghost text-xs"
                >
                  <Download size={13} /> Download
                </button>
                <button data-testid="preview-close" onClick={closePreview}>
                  <X size={16} className="text-slate-400" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-slate-100 overflow-auto flex items-center justify-center">
              {preview.kind === "image" ? (
                <img src={preview.url} alt={preview.doc?.file_name} className="max-w-full max-h-full object-contain" />
              ) : (
                <iframe src={preview.url} title={preview.doc?.file_name} className="w-full h-full border-0" />
              )}
            </div>
          </div>
        </div>
      )}

      {editForm.open && (
        <div
          data-testid="edit-doc-modal"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !editForm.saving && setEditForm(EMPTY_EDIT)}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="font-bold text-sm truncate pr-3">Edit — {editForm.doc?.file_name}</div>
              <button onClick={() => setEditForm(EMPTY_EDIT)}>
                <X size={16} className="text-slate-400" />
              </button>
            </div>
            <div className="p-5 space-y-3 max-h-[65vh] overflow-y-auto">
              <Field label="Short description">
                <input
                  data-testid="edit-description"
                  type="text"
                  maxLength={300}
                  className="input"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                />
              </Field>
              <Field label="Category">
                <select
                  data-testid="edit-category"
                  className="input"
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                >
                  <option value="">— None —</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
              {RECEIPT_FOLDERS.includes(editForm.doc?.folder) && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Amount (UGX)">
                    <input
                      data-testid="edit-amount"
                      type="number"
                      min="0"
                      className="input"
                      value={editForm.amount}
                      onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                    />
                  </Field>
                  <Field label="Receipt date">
                    <input
                      data-testid="edit-doc-date"
                      type="date"
                      className="input"
                      value={editForm.doc_date}
                      onChange={(e) => setEditForm({ ...editForm, doc_date: e.target.value })}
                    />
                  </Field>
                </div>
              )}
              <Field label="Expiration">
                <div className="flex gap-4 py-1">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      data-testid="edit-expires-no"
                      type="radio"
                      name="edit-expires"
                      checked={editForm.expires === "no"}
                      onChange={() => setEditForm({ ...editForm, expires: "no" })}
                    />
                    No expiration
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      data-testid="edit-expires-yes"
                      type="radio"
                      name="edit-expires"
                      checked={editForm.expires === "yes"}
                      onChange={() => setEditForm({ ...editForm, expires: "yes" })}
                    />
                    Has an expiry date
                  </label>
                </div>
              </Field>
              {editForm.expires === "yes" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Expiry date *">
                    <input
                      data-testid="edit-expiry"
                      type="date"
                      className="input"
                      value={editForm.expiry_date}
                      onChange={(e) => setEditForm({ ...editForm, expiry_date: e.target.value })}
                    />
                  </Field>
                  <Field label="Renew from">
                    <input
                      data-testid="edit-renew-date"
                      type="date"
                      max={editForm.expiry_date || undefined}
                      className="input"
                      value={editForm.renew_reminder_date}
                      onChange={(e) => setEditForm({ ...editForm, renew_reminder_date: e.target.value })}
                    />
                  </Field>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
              <button onClick={() => setEditForm(EMPTY_EDIT)} className="btn btn-ghost" disabled={editForm.saving}>
                Cancel
              </button>
              <button
                data-testid="edit-save"
                onClick={saveEdit}
                className="btn btn-primary"
                disabled={editForm.saving || (editForm.expires === "yes" && !editForm.expiry_date)}
              >
                {editForm.saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
